// static/js/components/ui/StructuredDataResult.jsx

/**
 * This JSX component renders structured data (JSON or CSV) with functionality to expand or collapse the displayed content.
 * It provides an interactive UI component where users can toggle the visibility of the complete data or just a preview.
 * The component uses state hooks for managing whether the data is expanded or collapsed and conditional rendering based
 * on the type of data provided ('json' or 'csv'). It includes error handling for JSON formatting and supports a fallback
 * for non-string data types to ensure they are appropriately displayed as strings.
 *
 * Usage:
 * This component is used in parts of an application where large chunks of structured data need to be displayed neatly
 * but compactly, with the option for the user to view more or less detail as needed. It is particularly useful in scenarios
 * where data visualization clarity and user interface interactivity are crucial.
 *
 * Features:
 * - Expand/Collapse functionality to switch between a brief preview and full content view.
 * - Dynamic rendering based on data type (JSON or CSV).
 * - Safe formatting and string conversion for displaying raw data.
 * - Styled with a minimalistic design to focus on data readability.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react'; // For expand/collapse icons
import DOMPurify from 'dompurify'; // for sanitizing JSON, Markdown, CSV, and stringified data.

// Type-specific label for expand button
const getExpandButtonLabel = (type) => {
  switch(type) {
    case 'csv':
      return 'CSV';
    case 'json':
      return 'JSON';
    default:
      return 'Content';
  }
};

/**
 * Component for displaying structured data results (JSON/CSV) with expand/collapse functionality.
 *
 * @param {Object} props - Component props
 * @param {Object|string} props.data - The structured data to display
 * @param {string} props.type - Data type ('json' or 'csv')
 */
const StructuredDataResult = ({ data, type }) => {
  // State for expand/collapse
  const [isExpanded, setIsExpanded] = useState(false);

  // Handler for toggling expansion
  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };


  /**
   * Safely format JSON data for display.
   *
   * @param {any} raw - The data to format as JSON
   * @returns {string} - A nicely spaced JSON string
   */
  function formatJSON(raw) {
    try {
      // First handle the JSON stringification
      const jsonString = JSON.stringify(raw, null, 2);
  
      // Apply comprehensive sanitization to prevent XSS attacks
      return DOMPurify.sanitize(jsonString, {
        FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onmouseout', 'onkeydown',
                      'onkeypress', 'onkeyup', 'formaction', 'javascript:', 'data:'],
        ALLOW_DATA_ATTR: false,
        USE_PROFILES: { html: true }
      });
    } catch (err) {
      // Enhanced sanitization for error case
      return DOMPurify.sanitize(String(raw), {
        FORBID_TAGS: ['*'], // Extremely strict in error cases - no HTML tags allowed
        FORBID_ATTR: ['*'], // No attributes allowed in error cases
        ALLOW_DATA_ATTR: false
      });
    }
  }

  // Render based on data type
  let displayValue;
  if (type === 'json') {
    displayValue = formatJSON(data);
  } else if (type === 'csv') {
    // If “data” is not guaranteed a string, convert it:
    displayValue = (typeof data === 'string')
      ? DOMPurify.sanitize(data) // sanitizes CSV data
      : DOMPurify.sanitize(JSON.stringify(data)); // sanitizes stringified data
  } else {
    // fallback: if we are here, we can just do a safe string conversion
    displayValue = (typeof data === 'string')
      ? DOMPurify.sanitize(data) // Sanitizes fallback string data
      : DOMPurify.sanitize(JSON.stringify(data)); // Sanitizes stringified data
  }

  // Calculate preview content
  const previewContent = displayValue.slice(0, 200) + (displayValue.length > 200 ? '...' : '');

  return (
    <div className="relative">
      {/* Content display with conditional height */}
      <pre className={`bg-primary-700/10 p-4 rounded-lg overflow-x-auto transition-all duration-300 ${
        isExpanded ? 'max-h-[48rem]' : 'max-h-32' // adjusts max height of expanded state
      }`}>
        <code className="text-sm text-primary-text">
          {isExpanded ? displayValue : previewContent}
        </code>
      </pre>
  
      {/* Expand/collapse toggle button at bottom of pane */}
      <button
        onClick={handleToggle}
        className="flex items-center space-x-2 mt-2 text-primary-text hover:text-primary-text/80 transition-colors text-xs"
      >
        {isExpanded ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        <span>{isExpanded ? 'Collapse' : 'Expand'} {getExpandButtonLabel(type)}</span>
      </button>
    </div>
  );

};

export default StructuredDataResult;
